import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useOrganizationSelector } from '@/hooks/use-organization-selector';
import { apiRequest } from '@/lib/queryClient';
import { getInitials } from '@/lib/utils';

/**
 * Organization selector component
 * - For contractors: Shows dropdown to select between organizations
 * - For employees: Shows read-only display of their organization
 */
export function OrganizationSelector() {
  const { t } = useTranslation();
  const {
    activeOrganizationId,
    activeOrganizationName,
    selectedOrgId,
    setSelectedOrgIdAndReset,
    organizations,
    canSelect,
  } = useOrganizationSelector();

  // Fetch organization logo for the active organization
  const { data: orgMeResponse } = useQuery({
    queryKey: ["/api/orgs/me", activeOrganizationId],
    queryFn: async () => {
      const url = activeOrganizationId 
        ? `/api/orgs/me?organizationId=${encodeURIComponent(activeOrganizationId)}`
        : '/api/orgs/me';
      const res = await apiRequest(url);
      return await res.json();
    },
    staleTime: 60_000,
    retry: false,
    enabled: !!activeOrganizationId,
  });
  const orgInfo = (orgMeResponse as any)?.data || null;
  const orgLogoUrl = orgInfo?.logoUrl || null;
  const orgInitials = activeOrganizationName ? getInitials(activeOrganizationName) : 'O';

  // Render organization logo or fallback icon
  const renderLogo = () => {
    if (orgLogoUrl) {
      return (
        <img
          src={orgLogoUrl}
          alt={activeOrganizationName || 'Organization logo'}
          className="h-4 w-4 object-cover rounded"
        />
      );
    }
    return (
      <div className="h-6 w-6 rounded-full bg-[var(--primary-blue)] text-white text-xs font-semibold flex items-center justify-center">
        {orgInitials}
      </div>
    );
  };

  // For employees or when no selection is possible, show read-only display
  if (!canSelect) {
    if (!activeOrganizationName) {
      return null; // Don't render if no organization
    }
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-md border border-slate-300">
        {renderLogo()}
        <span className="text-sm font-medium text-slate-700">{activeOrganizationName}</span>
      </div>
    );
  }

  // For contractors with multiple organizations: show selectable dropdown
  return (
    <div className="flex items-center gap-2">
      {renderLogo()}
      <Select
        value={selectedOrgId || undefined}
        onValueChange={(value) => setSelectedOrgIdAndReset(value)}
      >
        <SelectTrigger className="w-[200px] h-8 text-sm">
          <SelectValue placeholder={t('pages.dashboard:selectOrganization') || 'Select Organization'} />
        </SelectTrigger>
        <SelectContent>
          {organizations.map((org: any) => (
            <SelectItem key={org.id} value={org.id}>
              {org.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
